import type { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, Box, Checkbox, TextField } from '@mui/material';
import { MultiChipAutocomplete, StatusDot } from 'greener-ui-library';
import useUnitsConnectionStatus from 'hooks/useUnitsConnectionStatus';
import { GreenerUnit } from 'types';
import { DEVICE_TYPES, DEVICE_TYPE_IDS } from 'utils/constants';
import { getAssetLabel } from 'utils/formatters';
import { getStatusDotColor } from 'utils/units';

export default function UnitSelector({
  units,
  handleChange,
  selectedUnitExtId,
  unitType,
  fieldLabel,
  mode = 'single',
  selectedUnitExtIds,
  onMultiChange,
  showSelectAll = false,
  size = 'small',
}: {
  units: GreenerUnit[];
  handleChange?: (unit: GreenerUnit) => void;
  selectedUnitExtId?: string;
  unitType?: string;
  fieldLabel?: string;
  mode?: 'single' | 'multi';
  selectedUnitExtIds?: string[];
  onMultiChange?: (units: GreenerUnit[]) => void;
  showSelectAll?: boolean;
  size?: 'small' | 'medium';
}) {
  const { t } = useTranslation();
  const unitStatuses = useUnitsConnectionStatus();

  if (!units || units.length === 0) {
    return null;
  }

  const deviceTypeId = unitType === DEVICE_TYPES.BATTERY ? DEVICE_TYPE_IDS.BATTERY : DEVICE_TYPE_IDS.ENERGY_METER;

  const filteredUnits = unitType
    ? units.filter((unit) =>
        [unit.watcherType.deviceTypeId, unit.watcherType.deviceType?.deviceTypeId].includes(deviceTypeId)
      )
    : units;

  const renderDropdownOption = (
    props: HTMLAttributes<HTMLLIElement> & { key?: string },
    option: GreenerUnit,
    state: { selected: boolean }
  ) => {
    const { key, ...rest } = props;
    const isOnline = unitStatuses[option.externalIdentifier!];
    const dotColor = getStatusDotColor({ asset: option, isOnline });
    return (
      <Box key={key} component="li" {...rest} sx={{ display: 'flex', alignItems: 'center' }}>
        {mode === 'multi' && <Checkbox checked={state.selected} sx={{ mr: 1 }} />}
        <StatusDot color={dotColor} sx={{ mr: 1 }} />
        <span>{getAssetLabel(option)}</span>
      </Box>
    );
  };

  if (mode === 'multi') {
    const activeIds = selectedUnitExtIds ?? (selectedUnitExtId ? [selectedUnitExtId] : []);
    const selectedUnits = activeIds
      .map((id) => filteredUnits.find((unit) => unit.externalIdentifier === id))
      .filter(Boolean) as GreenerUnit[];

    return (
      <MultiChipAutocomplete<GreenerUnit>
        id="autocomplete-unit-selector"
        options={filteredUnits}
        value={selectedUnits}
        onChange={(newUnits) => onMultiChange?.(newUnits)}
        getOptionLabel={getAssetLabel}
        isOptionEqualToValue={(opt, val) => opt.externalIdentifier === val.externalIdentifier}
        getChipIcon={(unit) => {
          const isOnline = unitStatuses[unit.externalIdentifier!];
          const dotColor = getStatusDotColor({ asset: unit, isOnline });
          return <StatusDot color={dotColor} sx={{ ml: '6px' }} />;
        }}
        label={fieldLabel}
        renderOption={renderDropdownOption}
        selectAllLabel={showSelectAll ? t('entities:greenerUnit.selectAll') : undefined}
      />
    );
  }

  const selectedUnit = (filteredUnits.find((unit) => unit && unit.externalIdentifier === selectedUnitExtId) ??
    {}) as GreenerUnit;

  const onChangeUnit = (_: unknown, unit: GreenerUnit | null) => {
    unit && handleChange && unit.externalIdentifier && handleChange(unit);
  };

  return (
    <Autocomplete<GreenerUnit, false, true>
      id="autocomplete-unit-selector"
      data-testid="autocomplete-unit-selector"
      value={selectedUnit}
      options={filteredUnits}
      getOptionLabel={getAssetLabel}
      onChange={onChangeUnit}
      renderOption={renderDropdownOption}
      disableClearable
      renderInput={(params) => {
        const isOnline = unitStatuses[selectedUnit.externalIdentifier!];
        return (
          <TextField
            {...params}
            name="unit-selector"
            label={fieldLabel}
            variant="outlined"
            fullWidth
            InputLabelProps={{ shrink: true }}
            size={size}
            InputProps={{
              ...params.InputProps,
              startAdornment: (() => {
                const dotColor = getStatusDotColor({ asset: selectedUnit, isOnline });
                return <StatusDot color={dotColor} sx={{ ml: '10px' }} />;
              })(),
            }}
          />
        );
      }}
    />
  );
}
